import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, Undo2, Loader2, RotateCcw, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  create: "bg-blue-50 text-blue-700",
  update: "bg-amber-50 text-amber-700",
  delete: "bg-red-50 text-red-700",
  bulk_delete: "bg-red-100 text-red-800",
  bulk_update: "bg-amber-100 text-amber-800",
  lock: "bg-emerald-50 text-emerald-700",
  unlock: "bg-orange-50 text-orange-700",
  revert: "bg-violet-50 text-violet-700",
};

const fmtTime = ts => {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/**
 * Inline history list for any entity.
 * Usage: <EntityHistory collection="invoices" entityId={invoice.id} title="Invoice History" />
 */
export default function EntityHistory({ collection, entityId, title = "History", compact = false, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const load = useCallback(async () => {
    if (!collection || !entityId || !token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit-log/by-entity/${encodeURIComponent(collection)}/${encodeURIComponent(entityId)}`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data.rows || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [collection, entityId, token]);

  useEffect(() => { load(); }, [load]);

  const revert = async (id, summary) => {
    if (!window.confirm(`Revert this change?\n\n${summary}`)) return;
    setReverting(id);
    try {
      await axios.post(`${API}/audit-log/${id}/revert`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reverted");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally { setReverting(null); }
  };

  if (!entityId) return null;

  return (
    <div data-testid={`entity-history-${collection}-${entityId}`} className={compact ? "" : "border border-slate-200 rounded-lg bg-white overflow-hidden"}>
      {!compact && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
          <h3 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><History className="w-3.5 h-3.5 text-violet-500" />{title}</h3>
          <span className="text-[10px] text-slate-400">{rows.length} change{rows.length !== 1 ? "s" : ""}</span>
        </div>
      )}
      {loading ? (
        <div className="py-6 text-center text-slate-400 text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-slate-400 text-xs"><History className="w-6 h-6 mx-auto mb-1 text-slate-200" />No history yet</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map(r => (
            <li key={r.id} className="px-4 py-2 flex items-start gap-3 text-xs hover:bg-slate-50/50">
              <Badge className={`${ACTION_COLORS[r.action] || "bg-slate-100 text-slate-700"} border-0 text-[9px] mt-0.5 shrink-0`}>{r.action}</Badge>
              <div className="flex-1 min-w-0">
                <p className="text-slate-700 truncate" title={r.summary}>{r.summary}</p>
                <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                  <span>{fmtTime(r.timestamp)}</span>
                  <span>·</span>
                  <span>{r.user}</span>
                  {r.reverted && <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px] px-1 py-0 gap-0.5"><RotateCcw className="w-2 h-2" />Reverted</Badge>}
                </div>
              </div>
              {r.reversible && !r.reverted && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-violet-600 hover:bg-violet-50 shrink-0" disabled={reverting === r.id} onClick={() => revert(r.id, r.summary)} data-testid={`history-revert-${r.id}`}>
                  {reverting === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}Undo
                </Button>
              )}
              {r.reverted && <span className="text-[10px] text-violet-500 flex items-center gap-0.5 shrink-0"><CheckCircle2 className="w-3 h-3" /></span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
